import { Directive, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

export interface EventResponse {
    event: Event;
    ui: Element;
    data?: any;
}

@Directive({
    selector: '[draggable], [droppable], [sortable]'
})
export class DraggableDroppableSortableDirective implements OnInit, OnChanges, OnDestroy {

    /**
     * The draggable, droppable and sortable data.
     */
    @Input() data;

    /**
     * Indicates if the element is draggable.
     *
     * @type boolean
     */
    @Input() draggable: boolean;

    /**
     * Indicates if the element is droppable.
     *
     * @type boolean
     */
    @Input() droppable: boolean;

    /**
     * Indicates if the element is sortable.
     *
     * @type boolean
     */
    @Input() sortable: boolean;

    /**
     * Additional options.
     *
     * @type {{}}
     */
    @Input() options = {};

    @Input() orderKey: string;

    /**
     * The draggable, droppable and sortable create event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() create: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The draggable and sortable start event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() start: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The draggable and sortable stop event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() stop: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The droppable and sortable activate event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() activate: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The droppable and sortable deactivate event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() deactivate: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The draggable drag event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() drag: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The droppable drop event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() drop: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The sortable update event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() update: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The sortable remove event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() remove: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The sortable receive event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() receive: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The droppable and sortable over event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() over: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The droppable and sortable out event emitter.
     *
     * @type {EventEmitter<EventResponse>}
     */
    @Output() out: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * @type {EventEmitter<any>}
     * @deprecated
     */
    @Output() created: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * @type {EventEmitter<any>}
     * @deprecated
     */
    @Output() dropped: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * @type {EventEmitter<any>}
     * @deprecated
     */
    @Output() removed: EventEmitter<EventResponse> = new EventEmitter();

    /**
     * The item collection value.
     */
    public items;

    /**
     * The single item value.
     */
    public item;

    public settings: { [key: string]: { [key: string]: any } } = {};

    public instances: { [key: string]: any } = {};

    constructor(protected el: ElementRef, protected zone: NgZone) {
    }

    ngOnInit() {
        (function ($: any, undefined) {
            $.fn.multiDraggable = function (opts) {
                const initLeftOffset = []
                    , initTopOffset  = [];
                return this.each(function () {
                    $(this).on("mouseover", function () {
                        if (!$(this).data("init")) {
                            $(this).data("init", true).draggable(opts, {
                                start: function (event, ui) {
                                    const pos = $(this).position();
                                    $.each(opts.group || {}, function (key, value) {
                                        const elemPos = $(value).position();

                                        const x = pos.left - parseInt($(value).css('marginLeft'), 10);
                                        const y = pos.top - parseInt($(value).css('marginTop'), 10);

                                        initLeftOffset[key] = elemPos.left - pos.left - (key * 1);
                                        initTopOffset[key]  = elemPos.top - pos.top;
                                    });
                                    opts.startNative ? opts.startNative() : {};
                                },
                                drag: function (event, ui) {
                                    const pos = $(this).offset();
                                    $.each(opts.group || {}, function (key, value) {
                                        $(value).offset({
                                            left: pos.left + initLeftOffset[key],
                                            top: pos.top + initTopOffset[key]
                                        });
                                    });
                                    opts.dragNative ? opts.dragNative() : {};
                                },
                                stop: function (event, ui) {
                                    const pos = $(this).offset();
                                    $.each(opts.group || {}, function (key, value) {
                                        $(value).offset({
                                            left: pos.left + initLeftOffset[key],
                                            top: pos.top + initTopOffset[key]
                                        });
                                    });
                                    opts.stopNative ? opts.stopNative() : {};
                                },
                            });
                        }
                    });
                });
            };
        }(jQuery));
    }

    ngOnChanges(changes: SimpleChanges) {
        Object.keys(this.instances).forEach((type) => {
            this.instances[type][type]('destroy');
        });

        const data = changes.data ?
            changes.data.currentValue : null;

        if (!data) {
            return;
        }

        if (this.isDraggable()) {
            this.setUpDraggable();
        }

        if (this.isDroppable()) {
            this.setUpDroppable();
        }

        if (this.isSortable()) {
            this.setUpSortable();
        }

        this.attachDataOnChanges(changes);
    }

    protected attachDataOnChanges(changes: SimpleChanges) {
        const data = changes.data;

        if (!(data && data.currentValue)) {
            return;
        }

        const value = data.currentValue;

        if (this.isSortable()) {
            this.items = value;

            this.attachDataToElements();
        }

        if (this.isDraggable()) {
            this.item = value;

            this.attachDataToElement();
        }
    }

    protected isSortable() {
        return this.isBooleanAttributeSet(
            'sortable'
        );
    }

    protected isDraggable() {
        return this.isBooleanAttributeSet(
            'draggable'
        );
    }

    protected isDroppable() {
        return this.isBooleanAttributeSet(
            'droppable'
        );
    }

    protected isBooleanAttributeSet(name: string) {
        return this[name] !== undefined && this[name] !== false
            || this[name] === true;
    }

    public groupElement(id) {
        return $('.group' + id);
    }

    protected setUpDraggable() {
        const options: any = {
            placeholder: 'chassis-drag-drop-placeholder',
            forcePlaceholderSize: true,
            refreshPositions: true,
        };

        const events = {
            create: (event, ui) => {
                this.create.emit({
                    event: event,
                    ui: ui,
                });
            },
            drag: (event, ui) => {
                this.drag.emit({
                    event: event,
                    ui: ui,
                });
            },
            start: (event, ui) => {
                this.start.emit({
                    event: event,
                    ui: ui,
                    data: {
                        // draggable: $(ui.draggable[0]).data('drag-drop-item'),
                    }
                });
            },
            stop: (event, ui) => {
                this.stop.emit({
                    event: event,
                    ui: ui,
                    data: {
                        // draggable: $(ui.draggable[0]).data('drag-drop-item'),
                    }
                });
            },
        };

        const settings = Object.assign(events,
            Object.assign(options, this.options));

        settings.group = this.groupElement(settings.group);

        this.settings['draggable'] = settings;

        this.initJQuery('draggable', settings);
    }

    protected setUpDroppable() {
        const options = {
            placeholder: 'chassis-drag-drop-placeholder',
            forcePlaceholderSize: true,
        };

        const events = {
            create: (event, ui) => {
                this.create.emit({
                    event: event,
                    ui: ui,
                });
            },
            start: (event, ui) => {
                this.start.emit({
                    event: event,
                    ui: ui,
                });
            },
            stop: (event, ui) => {
                this.stop.emit({
                    event: event,
                    ui: ui,
                });
            },
            activate: (event, ui) => {

                this.activate.emit({
                    event: event,
                    ui: ui,
                });
            },
            deactivate: (event, ui) => {
                this.deactivate.emit({
                    event: event,
                    ui: ui,
                });
            },
            drop: (event, ui) => {
                const draggable = $(ui.draggable[0]).data('drag-drop-item');

                this.drop.emit({
                    event: event,
                    ui: ui,
                    data: {
                        draggable: draggable,
                        droppable: this.item,
                    }
                });
            },
            over: (event, ui) => {
                this.over.emit({
                    event: event,
                    ui: ui,
                });
            },
            out: (event, ui) => {
                this.out.emit({
                    event: event,
                    ui: ui,
                });
            },
        };

        const settings = Object.assign(events,
            Object.assign(options, this.options));

        this.settings['droppable'] = settings;

        this.initJQuery('droppable', settings);
    }

    protected setUpSortable() {
        const options = {
            placeholder: 'chassis-drag-drop-placeholder',
            forcePlaceholderSize: true,
            refreshPositions: true,
        };

        const events = {
            create: (event, ui) => {
                this.create.emit({
                    event: event,
                    ui: ui,
                });
            },
            start: (event, ui) => {
                this.start.emit({
                    event: event,
                    ui: ui,
                });
            },
            stop: (event, ui) => {
                this.stop.emit({
                    event: event,
                    ui: ui,
                    data: {
                        sortable: this.items,
                        serialized: this.instances['sortable'].sortable("toArray", {attribute: 'id'}),
                    }
                });
            },
            update: (event, ui) => {
                this.update.emit({
                    event: event,
                    ui: ui,
                });
            },
            remove: (event, ui) => {
                this.remove.emit({
                    event: event,
                    ui: ui,
                    data: {
                        data: ui.item.data('drag-drop-item'),
                        item: ui.item,
                        index: ui.item.index(),
                    }
                });
            },
            receive: (event, ui) => {
                this.receive.emit({
                    event: event,
                    ui: ui,
                    data: {
                        data: ui.item.data('drag-drop-item'),
                        item: ui.item,
                        index: ui.item.index(),
                    }
                });
            },
            activate: (event, ui) => {
                this.activate.emit({
                    event: event,
                    ui: ui,
                });
            },
            deactivate: (event, ui) => {
                this.deactivate.emit({
                    event: event,
                    ui: ui,
                });
            }
        };

        const settings = Object.assign(events,
            Object.assign(options, this.options));

        this.settings['sortable'] = settings;

        this.initJQuery('sortable', settings);
    }

    /**
     * Initialize the JQuery functionality.
     *
     * @param {string} type
     * @param options
     */
    protected initJQuery(type: string, options: { [key: string]: any }) {
        this.zone.runOutsideAngular(() => {
            this.instances[type] = (<any>$(this.el.nativeElement))[type](options);
        });
    }

    /**
     * Get drag and drop DOM elements
     *
     * @returns {Promise<T>|Promise}
     */
    protected getDragDropElements() {
        $(this.settings.sortable.items, this.el.nativeElement);
    }

    /**
     * Attach data to single item
     */
    protected attachDataToElement() {
        this.zone.runOutsideAngular(() => {
            $(this.el.nativeElement).data('drag-drop-item', this.item);
        });
    }

    /**
     * Attach data from items to DOM elements
     */
    protected attachDataToElements() {
        // this.getDragDropElements()
        //     .then(elements => {
        //         let i = 0;
        //
        //         for (const item of this.items) {
        //             $(elements[i]).data('drag-drop-item', item);
        //
        //             if (this.orderKey) {
        //                 item[this.orderKey] = i;
        //             }
        //
        //             i++;
        //         }
        //     });
    }

    ngOnDestroy() {
        Object.keys(this.instances).forEach((type) => {
            this.instances[type][type]('destroy');
        });
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/support/directives/draggable-droppable-sortable.directive.ts